import type { DestinationTypeValue, SaslMechanismValue } from "@/lib/schemas"
import { saslMechanismValues } from "@/lib/schemas"

interface TypeConfigFields {
  sqsQueueUrl?: string
  sqsRegion?: string
  sqsAccessKeyId?: string
  sqsSecretAccessKey?: string
  ebEventBusName?: string
  ebSource?: string
  ebDetailType?: string
  ebRegion?: string
  psProjectId?: string
  psTopicId?: string
  psCredentialsJson?: string
  kafkaBrokers?: string
  kafkaTopic?: string
  kafkaSaslMechanism?: SaslMechanismValue
  kafkaSaslUsername?: string
  kafkaSaslPassword?: string
  rmqUrl?: string
  rmqExchange?: string
  rmqRoutingKey?: string
}

export function buildTypeConfig(destType: DestinationTypeValue, values: TypeConfigFields): Record<string, unknown> | undefined {
  if (destType === "HTTP") return undefined

  if (destType === "SQS") {
    const cfg: Record<string, unknown> = {
      queue_url: values.sqsQueueUrl ?? "",
      region: values.sqsRegion ?? "",
    }
    if (values.sqsAccessKeyId) cfg.access_key_id = values.sqsAccessKeyId
    if (values.sqsSecretAccessKey) cfg.secret_access_key = values.sqsSecretAccessKey
    return cfg
  }

  if (destType === "EVENTBRIDGE") {
    const cfg: Record<string, unknown> = {
      event_bus_name: values.ebEventBusName ?? "",
      region: values.ebRegion ?? "",
    }
    if (values.ebSource) cfg.source = values.ebSource
    if (values.ebDetailType) cfg.detail_type = values.ebDetailType
    return cfg
  }

  if (destType === "PUBSUB") {
    const cfg: Record<string, unknown> = {
      project_id: values.psProjectId ?? "",
      topic_id: values.psTopicId ?? "",
    }
    if (values.psCredentialsJson) cfg.credentials_json = values.psCredentialsJson
    return cfg
  }

  if (destType === "KAFKA") {
    const brokersList = (values.kafkaBrokers ?? "").split(",").map((b) => b.trim()).filter(Boolean)
    const cfg: Record<string, unknown> = {
      brokers: brokersList,
      topic: values.kafkaTopic ?? "",
    }
    if (values.kafkaSaslMechanism) {
      cfg.sasl_mechanism = values.kafkaSaslMechanism
      if (values.kafkaSaslUsername) cfg.sasl_username = values.kafkaSaslUsername
      if (values.kafkaSaslPassword) cfg.sasl_password = values.kafkaSaslPassword
    }
    return cfg
  }

  if (destType === "RABBITMQ") {
    const cfg: Record<string, unknown> = {
      url: values.rmqUrl ?? "",
      exchange: values.rmqExchange ?? "",
    }
    if (values.rmqRoutingKey) cfg.routing_key = values.rmqRoutingKey
    return cfg
  }

  return undefined
}

function str(val: unknown): string {
  return typeof val === "string" ? val : ""
}

function strArr(val: unknown): string {
  if (Array.isArray(val)) return val.join(", ")
  return typeof val === "string" ? val : ""
}

export function extractTypeConfigFields(destType: DestinationTypeValue, config: Record<string, unknown> | null | undefined): TypeConfigFields {
  if (!config || destType === "HTTP") return {}

  if (destType === "SQS") {
    return {
      sqsQueueUrl: str(config.queue_url),
      sqsRegion: str(config.region),
      sqsAccessKeyId: str(config.access_key_id),
      sqsSecretAccessKey: str(config.secret_access_key),
    }
  }

  if (destType === "EVENTBRIDGE") {
    return {
      ebEventBusName: str(config.event_bus_name),
      ebSource: str(config.source),
      ebDetailType: str(config.detail_type),
      ebRegion: str(config.region),
    }
  }

  if (destType === "PUBSUB") {
    return {
      psProjectId: str(config.project_id),
      psTopicId: str(config.topic_id),
      psCredentialsJson: str(config.credentials_json),
    }
  }

  if (destType === "KAFKA") {
    const rawMechanism = str(config.sasl_mechanism)
    const mechanism: SaslMechanismValue = (saslMechanismValues as readonly string[]).includes(rawMechanism)
      ? rawMechanism as SaslMechanismValue
      : ""
    return {
      kafkaBrokers: strArr(config.brokers),
      kafkaTopic: str(config.topic),
      kafkaSaslMechanism: mechanism,
      kafkaSaslUsername: str(config.sasl_username),
      kafkaSaslPassword: str(config.sasl_password),
    }
  }

  if (destType === "RABBITMQ") {
    return {
      rmqUrl: str(config.url),
      rmqExchange: str(config.exchange),
      rmqRoutingKey: str(config.routing_key),
    }
  }

  return {}
}
